import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { AuthAPI } from "../../api/authAPI";
import AuthLayout from "../../components/AuthLayout";
import AuthProgressBar from "../../components/AuthProgressBar";
import { useOnboarding } from "../../context/OnboardingContext";
import { useAuth } from "../../context/AuthContext";
import { useFeedback } from "../../context/FeedbackContext";
import BrandField from "./BrandField";
import ReviewStatusScreen from "./ReviewStatusScreen";
import { USER_IN_REVIEW, USER_IS_WAITLISTED } from "../../utils/constants";
import {
  COUNTRY_CODE_OPTIONS,
  DEFAULT_COUNTRY_CODE,
  isValidPhoneNumber,
} from "../../utils/phoneUtils";
const DESIGNATION_OPTIONS = [
  "Founder / Owner",
  "C-suite (CEO / CMO / COO)",
  "VP / Director — Marketing & Growth",
  "Head / Manager - Influencer & Creator Marketing",
  "Head / Manager - Performance Marketing",
  "Brand Marketing Manager",
  "Social Media / Community Manager",
  "E-commerce / D2C Manager (Shopify/Marketplace)",
  "Partnerships / Business Development",
  "Product / Category Manager",
  "Agency / Partner (on behalf of brand)",
  "Other",
];
const HEAR_ABOUT_US_OPTIONS = [
  "Another brand",
  "Content Creator",
  "Social Media Post",
  "Ad",
  "Internal Team member",
  "Other",
];

const fieldLabelSx = {
  fontSize: 12,
  lineHeight: "18px",
  letterSpacing: "0.2px",
  fontWeight: 500,
  color: "#000",
};

export default function OnboardingStep1() {
  const navigate = useNavigate();
  const { email, step1Data, setStep1Data } = useOnboarding();
  const { logout, user } = useAuth();
  const { openFeedbackModal } = useFeedback();

  const [firstName, setFirstName] = useState(step1Data?.firstName || "");
  const [lastName, setLastName] = useState(step1Data?.lastName || "");
  const [designation, setDesignation] = useState(step1Data?.designation || "");
  const [designationOther, setDesignationOther] = useState(
    step1Data?.designationOther || "",
  );
  const [countryCode, setCountryCode] = useState(
    step1Data?.countryCode || DEFAULT_COUNTRY_CODE,
  );
  const [contactNumber, setContactNumber] = useState(
    step1Data?.contactNumber || "",
  );
  const [hearAboutUs, setHearAboutUs] = useState(step1Data?.hearAboutUs || "");
  const [hearAboutUsOther, setHearAboutUsOther] = useState(
    step1Data?.hearAboutUsOther || "",
  );
  const [brandName, setBrandName] = useState(step1Data?.brandName || "");
  const [brandWebsite, setBrandWebsite] = useState(
    step1Data?.brandWebsite || "",
  );
  const [errors, setErrors] = useState({});
  const [nextLoading, setNextLoading] = useState(false);

  const signedInEmail = useMemo(() => email || "", [email]);

  useEffect(() => {
    if (!signedInEmail) {
      navigate("/login", { replace: true });
    }
  }, [signedInEmail, navigate]);

  const isInReview =
    user?.data?.status === USER_IN_REVIEW ||
    user?.data?.status === USER_IS_WAITLISTED;

  const setBrandError = (field, message, payload) =>
    setErrors((prev) => ({
      ...prev,
      [field]: message,
      [`${field}Meta`]: payload && {
        duplicateType: payload.duplicate_type,
        supportWidget: Boolean(payload.support_widget),
      },
    }));

  const validateField = async (fieldType, value) => {
    if (!value?.trim()) {
      return;
    }
    const field = fieldType === "brand_name" ? "brandName" : "brandWebsite";
    try {
      await AuthAPI.validateOnboardingField(fieldType, value.trim());
      setBrandError(field, "", null);
    } catch (error) {
      const payload = error?.response?.data || {};
      setBrandError(field, payload.message || "This field is invalid", payload);
    }
  };

  const contactSupport = () =>
    openFeedbackModal("feedback", { brand_name: brandName.trim() });

  const validate = () => {
    const nextErrors = {};
    if (!firstName.trim()) {
      nextErrors.firstName = "First name is required";
    }
    if (!lastName.trim()) {
      nextErrors.lastName = "Last name is required";
    }
    if (!designation) {
      nextErrors.designation = "Your role is required";
    }
    if (designation === "Other" && !designationOther.trim()) {
      nextErrors.designationOther = "Please enter your role";
    }
    const phoneDigits = contactNumber.replace(/\s+/g, "");
    if (!phoneDigits) {
      nextErrors.contactNumber = "Contact number is required";
    } else if (!isValidPhoneNumber(countryCode, phoneDigits)) {
      nextErrors.contactNumber = "Enter a valid phone number";
    }
    if (!hearAboutUs) {
      nextErrors.hearAboutUs = "Please select how you heard about us";
    }
    if (hearAboutUs === "Other" && !hearAboutUsOther.trim()) {
      nextErrors.hearAboutUsOther = "Please specify referral source";
    }
    if (!brandName.trim()) {
      nextErrors.brandName = "Brand name is required";
    } else if (errors.brandName) {
      nextErrors.brandName = errors.brandName;
    }
    if (!brandWebsite.trim()) {
      nextErrors.brandWebsite = "Brand website is required";
    } else if (errors.brandWebsite) {
      nextErrors.brandWebsite = errors.brandWebsite;
    }
    setErrors((prev) => ({ ...prev, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const onNext = async () => {
    if (!validate()) {
      return;
    }
    const website = /^https?:\/\//i.test(brandWebsite.trim())
      ? brandWebsite.trim()
      : `https://${brandWebsite.trim()}`;
    setNextLoading(true);
    try {
      await AuthAPI.startSignupVerification({
        brandName: brandName.trim(),
        brandWebsite: website,
        designation:
          designation === "Other" ? designationOther.trim() : designation,
      });
    } catch (error) {
      const payload = error?.response?.data || {};
      // Only a 400 with a message blocks; network/5xx must not stop signup.
      if (error?.response?.status === 400 && payload.message) {
        if (payload.duplicate_type === "brand_name") {
          setBrandError("brandName", payload.message, payload);
        } else if (
          payload.duplicate_type === "website" ||
          /website/i.test(payload.message)
        ) {
          setBrandError("brandWebsite", payload.message, payload);
        } else {
          enqueueSnackbar(payload.message, { variant: "error" });
        }
        setNextLoading(false);
        return;
      }
    }
    setNextLoading(false);
    setStep1Data({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      designation,
      designationOther,
      countryCode,
      contactNumber: contactNumber.replace(/\s+/g, ""),
      hearAboutUs,
      hearAboutUsOther,
      brandName: brandName.trim(),
      brandWebsite: website,
      workEmail: signedInEmail,
    });
    navigate("/register/brand");
  };

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const onPrevious = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  if (!signedInEmail) {
    return null;
  }

  if (isInReview) {
    return <ReviewStatusScreen />;
  }

  return (
    <AuthLayout>
      <Box>
        <AuthProgressBar step={1} />
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography
            sx={{ fontSize: 32, fontWeight: 600, lineHeight: "32px" }}
          >
            Basic Details
          </Typography>

          <Stack spacing={1}>
            <Typography sx={fieldLabelSx}>First name</Typography>
            <TextField
              size="small"
              placeholder="Enter first name"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                if (errors.firstName) {
                  setErrors((prev) => ({ ...prev, firstName: "" }));
                }
              }}
              error={Boolean(errors.firstName)}
              helperText={errors.firstName}
            />
          </Stack>

          <Stack spacing={1}>
            <Typography sx={fieldLabelSx}>Last name</Typography>
            <TextField
              size="small"
              placeholder="Enter last name"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                if (errors.lastName) {
                  setErrors((prev) => ({ ...prev, lastName: "" }));
                }
              }}
              error={Boolean(errors.lastName)}
              helperText={errors.lastName}
            />
          </Stack>

          <FormControl size="small" error={Boolean(errors.designation)}>
            <InputLabel>Your Role</InputLabel>
            <Select
              value={designation}
              label="Your Role"
              data-cy="designation-select"
              onChange={(e) => {
                setDesignation(e.target.value);
                if (errors.designation)
                  setErrors((prev) => ({ ...prev, designation: "" }));
              }}
            >
              {DESIGNATION_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
            {errors.designation ? (
              <FormHelperText>{errors.designation}</FormHelperText>
            ) : null}
          </FormControl>

          {designation === "Other" ? (
            <TextField
              size="small"
              label="Your Role (Other)"
              placeholder="Enter your role"
              value={designationOther}
              onChange={(e) => {
                setDesignationOther(e.target.value);
                if (errors.designationOther)
                  setErrors((prev) => ({ ...prev, designationOther: "" }));
              }}
              error={Boolean(errors.designationOther)}
              helperText={errors.designationOther}
            />
          ) : null}

          <Stack spacing={1}>
            <Typography sx={fieldLabelSx}>Contact number</Typography>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "112px 1fr", gap: 1 }}
            >
              <Autocomplete
                size="small"
                disableClearable
                options={COUNTRY_CODE_OPTIONS}
                value={countryCode}
                onChange={(_, newValue) =>
                  setCountryCode(newValue || DEFAULT_COUNTRY_CODE)
                }
                renderInput={(params) => <TextField {...params} />}
              />
              <TextField
                size="small"
                placeholder="Enter contact here"
                value={contactNumber}
                onChange={(e) => {
                  setContactNumber(e.target.value.replace(/[^0-9\s]/g, ""));
                  if (errors.contactNumber)
                    setErrors((prev) => ({ ...prev, contactNumber: "" }));
                }}
                error={Boolean(errors.contactNumber)}
                helperText={errors.contactNumber}
              />
            </Box>
          </Stack>

          <TextField
            size="small"
            label="Work email"
            value={signedInEmail}
            InputProps={{ readOnly: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#efefef",
                "& fieldset": { borderColor: "#8a8a8a" },
              },
            }}
          />

          <FormControl size="small" error={Boolean(errors.hearAboutUs)}>
            <InputLabel>Where did you hear about us?</InputLabel>
            <Select
              value={hearAboutUs}
              label="Where did you hear about us?"
              data-cy="hear-about-us-select"
              onChange={(e) => {
                setHearAboutUs(e.target.value);
                if (errors.hearAboutUs) {
                  setErrors((prev) => ({ ...prev, hearAboutUs: "" }));
                }
              }}
            >
              {HEAR_ABOUT_US_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
            {errors.hearAboutUs ? (
              <FormHelperText>{errors.hearAboutUs}</FormHelperText>
            ) : null}
          </FormControl>

          {hearAboutUs === "Other" ? (
            <TextField
              size="small"
              label="Referral source (Other)"
              placeholder="Please specify"
              value={hearAboutUsOther}
              onChange={(e) => {
                setHearAboutUsOther(e.target.value);
                if (errors.hearAboutUsOther)
                  setErrors((prev) => ({ ...prev, hearAboutUsOther: "" }));
              }}
              error={Boolean(errors.hearAboutUsOther)}
              helperText={errors.hearAboutUsOther}
            />
          ) : null}

          <BrandField
            label="Brand Name"
            placeholder="Enter brand name here"
            value={brandName}
            onChange={(e) => {
              setBrandName(e.target.value);
              if (errors.brandName || errors.brandNameMeta)
                setErrors((prev) => ({
                  ...prev,
                  brandName: "",
                  brandNameMeta: null,
                }));
            }}
            onBlur={() => validateField("brand_name", brandName)}
            error={errors.brandName}
            showSupport={errors.brandNameMeta?.supportWidget}
            onContactSupport={contactSupport}
          />

          <BrandField
            label="Brand Website"
            placeholder="Enter website link here"
            value={brandWebsite}
            onChange={(e) => {
              setBrandWebsite(e.target.value);
              if (errors.brandWebsite || errors.brandWebsiteMeta)
                setErrors((prev) => ({
                  ...prev,
                  brandWebsite: "",
                  brandWebsiteMeta: null,
                }));
            }}
            onBlur={() => validateField("website", brandWebsite)}
            error={errors.brandWebsite}
            showSupport={errors.brandWebsiteMeta?.supportWidget}
            onContactSupport={contactSupport}
          />

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Button
              variant="contained"
              onClick={onPrevious}
              sx={{
                backgroundColor: "#fce2dd",
                color: "#ee4d37",
                boxShadow: "none",
                borderRadius: "8px",
                height: 48,
                "&:hover": { backgroundColor: "#f8d5ce", boxShadow: "none" },
              }}
            >
              ← Previous
            </Button>
            <Button
              variant="contained"
              onClick={onNext}
              disabled={nextLoading}
              sx={{
                backgroundColor: "#ee4d37",
                borderRadius: "8px",
                height: 48,
                "&:hover": { backgroundColor: "#d94430" },
              }}
            >
              {nextLoading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Next →"
              )}
            </Button>
          </Box>
        </Stack>

        <Box
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            color: "#808080",
          }}
        >
          <Typography
            component="span"
            sx={{ fontSize: 14, lineHeight: "20px" }}
          >
            You are signed in as
          </Typography>
          <Typography
            component="span"
            sx={{
              color: "#0d0d0d",
              fontWeight: 600,
              fontSize: 14,
              lineHeight: "20px",
            }}
          >
            {signedInEmail}
          </Typography>
          <Typography component="span" sx={{ color: "#d1d1d1", fontSize: 12 }}>
            |
          </Typography>
          <Button
            onClick={onLogout}
            sx={{
              minWidth: "auto",
              p: 0,
              color: "#ee4d37",
              textTransform: "none",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "20px",
            }}
          >
            Log Out
          </Button>
        </Box>
      </Box>
    </AuthLayout>
  );
}
